import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import ListGroup from 'react-bootstrap/ListGroup';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'OcCommentsPanel';

/**
 * Comments panel. Pass `api` from useOcRequestWorkspace (createComment).
 * Client portal may pass a thin adapter or keep allowCompose false.
 *
 * A comment never changes request status. The server emails the current status
 * owner that a comment was added without including the body, and `contact_email`
 * is never a recipient — including for `client` visibility. Use the Request
 * clarification transition when the request should move to client clarification.
 */
const OcCommentsPanel = ({
  api,
  comments = [],
  onUpdated,
  allowCompose = true,
  defaultVisibility = 'internal',
  visibilityFilter = 'all',
}) => {
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState(defaultVisibility);
  const [saving, setSaving] = useState(false);

  const visible = comments.filter(c =>
    visibilityFilter === 'all' ? true : c.visibility === visibilityFilter
  );

  const handleSubmit = async e => {
    e.preventDefault();
    if (!body.trim() || !api?.createComment) return;
    setSaving(true);
    const result = await api.createComment({
      body: body.trim(),
      visibility,
    });
    setSaving(false);
    if (result.success) {
      logInfo(LOG_CONTEXT, 'comment created', {
        visibility,
        mode: api.mode,
      });
      setBody('');
      onUpdated?.(result.data);
      showToast('Comment added', 'success', 'top-right', 2500);
    } else {
      showToast(
        result.messages?.[0] || 'Failed to add comment',
        'error',
        'top-right',
        4000
      );
    }
  };

  return (
    <div>
      <h6>Comments</h6>
      {visible.length === 0 ? (
        <p className="text-muted small">No comments.</p>
      ) : (
        <ListGroup className="mb-3">
          {visible.map(c => (
            <ListGroup.Item key={c.id}>
              <div className="small text-muted">
                {c.visibility} · {c.author_username || c.author_email || '—'} ·{' '}
                {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
              </div>
              <div>{c.body}</div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {allowCompose && api?.createComment ? (
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-2">
            <Form.Control
              as="textarea"
              rows={2}
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Add a comment"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Select
              value={visibility}
              onChange={e => setVisibility(e.target.value)}
            >
              <option value="internal">Internal</option>
              <option value="client">Client-visible</option>
            </Form.Select>
            <Form.Text className="text-muted">
              Adding a comment does not change the request status. The status owner is
              notified that a comment was added; the text is not included in that email
              and is never emailed to the customer.
            </Form.Text>
          </Form.Group>
          <Button type="submit" size="sm" disabled={saving || !body.trim()}>
            {saving ? 'Saving…' : 'Add comment'}
          </Button>
        </Form>
      ) : null}
    </div>
  );
};

export default OcCommentsPanel;
